

import { Card, CardContent } from "@/components/ui/card";
import { useI18n } from '../i18n/i18n';
import { useSEO } from '../hooks/useSEO';

const CookiesPolicyPage = () => {
  const { t } = useI18n();
  
  // SEO Configuration
  const seoContent = useSEO('legal', 'cookies');

  return (
    <div className="fade-in relative py-12 bg-[#F9FAFB]">
      {seoContent}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-bl from-orange-100/30 to-transparent rounded-bl-full -z-10" />
      <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-gradient-to-tr from-orange-100/30 to-transparent rounded-tr-full -z-10" />

      <div className="container mx-auto px-4 md:px-6">
        <Card className="max-w-4xl mx-auto">
          <CardContent className="p-6 md:p-8">
            <h1 className="text-3xl font-bold text-neutral-800 mb-6">
              {t('cookies.title')}
            </h1>
            <p className="text-sm text-neutral-600 mb-8">
              {t('cookies.lastUpdated')}: 04/05/2025
            </p>

            <div className="prose prose-neutral max-w-none">
              <p className="mb-6">{t('cookies.introduction')}</p>

              <h2 className="text-xl font-semibold mt-8 mb-4">{t('cookies.section1.title')}</h2>
              <p className="mb-4">{t('cookies.section1.content')}</p>

              <h2 className="text-xl font-semibold mt-8 mb-4">{t('cookies.section2.title')}</h2>
              <p className="mb-4">{t('cookies.section2.content')}</p>

              <h3 className="text-lg font-semibold mt-6 mb-3">{t('cookies.section2.categories.title')}</h3>
              
              <h4 className="text-base font-semibold mt-4 mb-2">{t('cookies.section2.categories.essential.name')}</h4>
              <p className="mb-4">{t('cookies.section2.categories.essential.description')}</p>

              <h4 className="text-base font-semibold mt-4 mb-2">{t('cookies.section2.categories.preferences.name')}</h4>
              <p className="mb-4">{t('cookies.section2.categories.preferences.description')}</p>

              <h4 className="text-base font-semibold mt-4 mb-2">{t('cookies.section2.categories.analytics.name')}</h4>
              <p className="mb-4">{t('cookies.section2.categories.analytics.description')}</p>

              <h4 className="text-base font-semibold mt-4 mb-2">{t('cookies.section2.categories.marketing.name')}</h4>
              <p className="mb-6">{t('cookies.section2.categories.marketing.description')}</p>

              <h2 className="text-xl font-semibold mt-8 mb-4">{t('cookies.section3.title')}</h2>
              <p className="mb-4">{t('cookies.section3.content')}</p>
              <div className="overflow-x-auto mb-6">
                <table className="min-w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2">{t('cookies.section3.table.cookie')}</th>
                      <th className="border border-gray-300 px-4 py-2">{t('cookies.section3.table.provider')}</th>
                      <th className="border border-gray-300 px-4 py-2">{t('cookies.section3.table.purpose')}</th>
                      <th className="border border-gray-300 px-4 py-2">{t('cookies.section3.table.expiry')}</th>
                      <th className="border border-gray-300 px-4 py-2">{t('cookies.section3.table.type')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">finanzly_session</td>
                      <td className="border border-gray-300 px-4 py-2">Finanzly</td>
                      <td className="border border-gray-300 px-4 py-2">{t('cookies.section3.table.sessionDescription')}</td>
                      <td className="border border-gray-300 px-4 py-2">{t('cookies.section3.table.session')}</td>
                      <td className="border border-gray-300 px-4 py-2">HTTP</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">language_preference</td>
                      <td className="border border-gray-300 px-4 py-2">Finanzly</td>
                      <td className="border border-gray-300 px-4 py-2">{t('cookies.section3.table.languageDescription')}</td>
                      <td className="border border-gray-300 px-4 py-2">1 {t('cookies.section3.table.year')}</td>
                      <td className="border border-gray-300 px-4 py-2">HTTP</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">_ga</td>
                      <td className="border border-gray-300 px-4 py-2">Google Analytics</td>
                      <td className="border border-gray-300 px-4 py-2">{t('cookies.section3.table.analyticsDescription')}</td>
                      <td className="border border-gray-300 px-4 py-2">2 {t('cookies.section3.table.years')}</td>
                      <td className="border border-gray-300 px-4 py-2">HTTP</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-xl font-semibold mt-8 mb-4">{t('cookies.section4.title')}</h2>
              <p className="mb-4">{t('cookies.section4.content')}</p>
              <p className="mb-4">{t('cookies.section4.browsers')}</p>
              <ul className="list-disc pl-6 mb-6">
                <li className="mb-2">Chrome: {t('cookies.section4.chrome')}</li>
                <li className="mb-2">Firefox: {t('cookies.section4.firefox')}</li>
                <li className="mb-2">Safari: {t('cookies.section4.safari')}</li>
                <li className="mb-2">Edge: {t('cookies.section4.edge')}</li>
                <li>Opera: {t('cookies.section4.opera')}</li>
              </ul>

              <div className="bg-orange-50 p-4 rounded-lg my-6">
                <h4 className="font-semibold mb-2">{t('cookies.section4.note.title')}</h4>
                <p>{t('cookies.section4.note.content')}</p>
              </div>

              <h2 className="text-xl font-semibold mt-8 mb-4">{t('cookies.section5.title')}</h2>
              <p className="mb-4">{t('cookies.section5.content')}</p>

              <h2 className="text-xl font-semibold mt-8 mb-4">{t('cookies.section6.title')}</h2>
              <p className="mb-4">{t('cookies.section6.content')}</p>

              <h2 className="text-xl font-semibold mt-8 mb-4">{t('cookies.contact.title')}</h2>
              <p>{t('cookies.contact.content')}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CookiesPolicyPage;
